import React, { useState, useMemo } from 'react';
import { Camera, Image as ImageIcon, Users, DollarSign } from 'lucide-react';
import { Card } from '../ui';
import { Input } from '../ui';

export function CompositePhotoCalculator() {
  const [headcount, setHeadcount] = useState<number>(85);
  const [sittingFee, setSittingFee] = useState<number>(15); // per member
  const [masterFrameCost, setMasterFrameCost] = useState<number>(850);
  const [includeMemberCopies, setIncludeMemberCopies] = useState<boolean>(true);
  const [memberCopyCost, setMemberCopyCost] = useState<number>(25); // 11x14 copy

  const model = useMemo(() => {
    const totalSittingFees = headcount * sittingFee;
    const totalCopiesCost = includeMemberCopies ? headcount * memberCopyCost : 0;
    
    const totalCost = totalSittingFees + masterFrameCost + totalCopiesCost;
    const costPerMember = headcount > 0 ? totalCost / headcount : 0;

    return {
      totalSittingFees,
      totalCopiesCost,
      totalCost,
      costPerMember
    };
  }, [headcount, sittingFee, masterFrameCost, includeMemberCopies, memberCopyCost]);

  const formatCurrency = (val: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val);

  return (
    <div className="max-w-5xl mx-auto space-y-8 p-4">
      <div className="bg-gradient-to-r from-pink-600 to-rose-700 rounded-2xl p-8 text-white shadow-xl flex flex-col md:flex-row items-center justify-between gap-6">
        <div>
          <h2 className="text-2xl font-black flex items-center mb-2">
            <Camera className="w-6 h-6 mr-3 text-pink-200" />
            Composite Photo Pricing Estimator
          </h2>
          <p className="text-pink-100 max-w-xl">
            Don't get blindsided by hidden fees. Estimate the true cost of your chapter's annual composite photo, including sitting fees, master framing, and member copies.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        
        {/* Controls */}
        <Card className="p-6 space-y-6 border-slate-200 dark:border-slate-800 shadow-xl bg-white dark:bg-slate-900">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="text-sm font-semibold text-slate-500 uppercase flex items-center mb-2">
                <Users className="w-4 h-4 mr-2 text-pink-500" /> Total Chapter Headcount
              </label>
              <Input 
                type="number" 
                value={headcount} 
                onChange={(e) => setHeadcount(Number(e.target.value))}
                className="text-lg font-bold"
              />
            </div>
            
            <div>
              <label className="text-sm font-semibold text-slate-500 uppercase block mb-2">Sitting Fee Per Member ($)</label>
              <Input 
                type="number" 
                value={sittingFee} 
                onChange={(e) => setSittingFee(Number(e.target.value))}
              />
            </div>
            
            <div>
              <label className="text-sm font-semibold text-slate-500 uppercase block mb-2">Master Frame Cost ($)</label>
              <Input 
                type="number" 
                value={masterFrameCost} 
                onChange={(e) => setMasterFrameCost(Number(e.target.value))}
              />
            </div>
          </div>

          <div className="pt-6 border-t border-slate-100 dark:border-slate-800 space-y-4">
            <div className="flex items-center gap-3">
              <input
                id="includeCopies"
                type="checkbox"
                checked={includeMemberCopies}
                onChange={(e) => setIncludeMemberCopies(e.target.checked)}
                className="w-5 h-5 accent-pink-600 rounded cursor-pointer"
              />
              <label htmlFor="includeCopies" className="font-semibold text-slate-700 dark:text-slate-300 cursor-pointer">
                Include personal 11x14 copies for all members
              </label>
            </div>
            
            {includeMemberCopies && (
              <div className="pl-8">
                <label className="text-sm font-semibold text-slate-500 uppercase block mb-2">Copy Cost Per Member ($)</label>
                <Input 
                  type="number" 
                  value={memberCopyCost} 
                  onChange={(e) => setMemberCopyCost(Number(e.target.value))}
                  className="max-w-[150px]"
                />
              </div>
            )}
          </div>
        </Card>

        {/* Results */}
        <div className="space-y-6">
          <Card className="p-6 border-slate-200 dark:border-slate-800 shadow-xl bg-slate-900 text-white flex flex-col justify-center">
            <div className="flex justify-between items-start mb-6">
              <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider">Total Invoice Estimate</h3>
              <DollarSign className="w-6 h-6 text-pink-500" />
            </div>
            <div className="text-5xl font-black text-pink-400 tracking-tight">
              {formatCurrency(model.totalCost)}
            </div>
            <p className="text-slate-400 text-sm mt-2">
              Your per-member cost is <strong>{formatCurrency(model.costPerMember)}</strong>
            </p>
          </Card>

          <Card className="p-0 border-slate-200 dark:border-slate-800 shadow-xl bg-white dark:bg-slate-900 overflow-hidden">
            <div className="bg-slate-50 dark:bg-slate-800/50 p-4 border-b border-slate-100 dark:border-slate-800">
              <h3 className="font-bold flex items-center">
                <ImageIcon className="w-4 h-4 mr-2 text-slate-500" /> Itemized Breakdown
              </h3>
            </div>
            
            <div className="divide-y divide-slate-100 dark:divide-slate-800">
              <div className="p-4 flex items-center justify-between">
                <div>
                  <div className="font-bold text-slate-900 dark:text-white">Photography Sitting Fees</div>
                  <div className="text-sm text-slate-500">{headcount} members @ {formatCurrency(sittingFee)}/ea</div>
                </div>
                <div className="text-lg font-bold">
                  {formatCurrency(model.totalSittingFees)}
                </div>
              </div>
              
              <div className="p-4 flex items-center justify-between">
                <div>
                  <div className="font-bold text-slate-900 dark:text-white">Master Board & Custom Framing</div>
                  <div className="text-sm text-slate-500">For chapter house display</div>
                </div>
                <div className="text-lg font-bold">
                  {formatCurrency(masterFrameCost)}
                </div>
              </div>

              {includeMemberCopies && (
                <div className="p-4 flex items-center justify-between">
                  <div>
                    <div className="font-bold text-slate-900 dark:text-white">Personal Prints</div>
                    <div className="text-sm text-slate-500">{headcount} copies @ {formatCurrency(memberCopyCost)}/ea</div>
                  </div>
                  <div className="text-lg font-bold">
                    {formatCurrency(model.totalCopiesCost)}
                  </div>
                </div>
              )}
            </div>
          </Card>
        </div>

      </div>
    </div>
  );
}
